import React, { useMemo } from "react";
import { salesApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  ColumnChart,
  DonutChart,
  ErrorState,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import { PALETTE, buildKpis, formatDate, toDist } from "../reportUtils";

const DEFS = [
  {
    key: "totalLeads",
    label: "All leads",
    desc: "People who might buy",
    icon: "mdi:bullseye-arrow",
  },
  {
    key: "newLeads",
    label: "New leads",
    desc: "Added {p}",
    prev: "previousNewLeads",
    icon: "mdi:flash-outline",
  },
  {
    key: "qualifiedLeads",
    label: "Good leads",
    desc: "Worth following up {p}",
    icon: "mdi:shield-check-outline",
  },
  {
    key: "convertedLeads",
    label: "Became clients",
    desc: "Leads who bought {p}",
    icon: "mdi:account-convert-outline",
  },
  {
    key: "lostLeads",
    label: "Lost leads",
    desc: "Leads who said no {p}",
    good: "DOWN",
    icon: "mdi:close-octagon-outline",
  },
  {
    key: "conversionRate",
    label: "Success rate",
    desc: "Out of every 100 leads, how many bought",
    unit: "PERCENT",
    icon: "mdi:percent-outline",
  },
  {
    key: "avgConversionDays",
    label: "Days to win",
    desc: "Average days to turn a lead into a client",
    unit: "DAYS",
    good: "DOWN",
    icon: "mdi:calendar-clock-outline",
  },
  {
    key: "avgFirstResponseHours",
    label: "First reply time",
    desc: "Hours before we first reply",
    unit: "HOURS",
    good: "DOWN",
    icon: "mdi:reply-clock-outline",
  },
  {
    key: "unassignedLeads",
    label: "No owner",
    desc: "Leads nobody looks after yet",
    good: "DOWN",
    icon: "mdi:account-question-outline",
  },
  {
    key: "staleLeads",
    label: "Forgotten leads",
    desc: "No news for a few days",
    good: "DOWN",
    icon: "mdi:timer-sand",
  },
];

const leadListColumns = [
  cols.text("leadName", "Lead"),
  cols.text("industry", "Industry"),
  cols.text("assignedEmployeeName", "Owner"),
  cols.badge("status", "Status"),
  cols.num("daysOpen", "Days open"),
  cols.date("createdAt", "Added"),
];

const leadItems = (rows = []) =>
  rows.map((l) => ({
    id: l.leadId,
    title: l.leadName,
    subtitle: [l.industry, l.assignedEmployeeName || "Unassigned"]
      .filter(Boolean)
      .join(", "),
    badge: l.status,
    right: `${l.daysOpen ?? "—"} days open`,
  }));

const LeadsView = ({ params, setFilter, onLoaded, refreshKey }) => {
  const { view } = useReportContext();
  const { data, loading, error, reload } = useReportData(
    () => salesApi.leadSummary(params),
    [JSON.stringify(params), refreshKey],
    onLoaded,
  );
  const tableParams = useMemo(
    () => ({
      startDate: params.startDate,
      endDate: params.endDate,
      ownerId: params.ownerId,
      source: params.source,
      industry: params.industry,
      status: params.status,
    }),
    [params],
  );

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { meta = {}, charts = {}, lists = {} } = data;
  const currency = meta.currency || "INR";
  const kpis = buildKpis(data.kpis, DEFS);
  const stale = kpis.find((k) => k.key === "staleLeads")?.value || 0;
  const unassigned = kpis.find((k) => k.key === "unassignedLeads")?.value || 0;
  const trend = [
    { key: "newLeads", label: "New", color: PALETTE[0], type: "area" },
    {
      key: "convertedLeads",
      label: "Converted",
      color: PALETTE[1],
      type: "area",
    },
    { key: "lostLeads", label: "Lost", color: PALETTE[4], type: "area" },
  ];

  const columns = [
    {
      key: "leadName",
      header: "Lead",
      sortable: true,
      render: (v) => <strong>{v}</strong>,
    },
    { key: "industry", header: "Industry" },
    { key: "source", header: "Source" },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (v) => <StatusBadge value={v} />,
    },
    {
      key: "assignedEmployeeName",
      header: "Owner",
      render: (v) => v || <span className="rp-muted">Unassigned</span>,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (v) => formatDate(v),
    },
    { key: "daysOpen", header: "Days open", align: "right" },
    { key: "enquiryCount", header: "Enquiries", align: "right" },
    {
      key: "lastActivityAt",
      header: "Last activity",
      render: (v) => formatDate(v),
    },
    { key: "convertedAt", header: "Converted", render: (v) => formatDate(v) },
    { key: "lostReason", header: "Lost reason" },
  ];

  const base = tableParams;
  const created = { createdFrom: params.startDate, createdTo: params.endDate };
  const T = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns,
      fetcher: salesApi.leadTable,
      params: { ...base, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  // LeadsStatus is only AVAILABLE / OFFLINE, so "became a client" and "lost" are read from the lead's own fields.
  const converted = (r) => !!(r.convertedAt || r.convertedClientId);
  const details = {
    totalLeads: {
      scrollTo: "tbl-main",
      ...T("All leads", "These are all the leads you can see."),
    },
    newLeads: T("New leads", "These are the leads added {p}.", created),
    qualifiedLeads: T(
      "Leads with an enquiry",
      "These leads already have at least one enquiry.",
      {},
      { filter: (r) => Number(r.enquiryCount) > 0 },
    ),
    convertedLeads: T(
      "Leads who became clients",
      "These leads bought and became clients.",
      {},
      { filter: converted },
    ),
    lostLeads: T(
      "Lost leads",
      "These leads were given up, and the reason is shown.",
      {},
      { filter: (r) => !!r.lostReason },
    ),
    conversionRate: T(
      "Leads who became clients",
      "Out of the leads added {p}, these became clients.",
      created,
      { filter: converted },
    ),
    avgConversionDays: T(
      "Leads who became clients",
      "Each of these took some days to turn into a client. The average of those days is shown.",
      {},
      { filter: converted },
    ),
    avgFirstResponseHours: { scrollTo: "lead-owners" },
    unassignedLeads: T(
      "Leads with no owner",
      "Nobody looks after these leads yet.",
      {},
      { filter: (r) => !r.assignedEmployeeName },
    ),
    staleLeads: { scrollTo: "stale-leads" },
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          unassigned > 0 && {
            tone: "warn",
            icon: "mdi:account-question-outline",
            text: `${unassigned} leads have no owner`,
          },
          stale > 0 && {
            tone: "bad",
            icon: "mdi:timer-alert-outline",
            text: `${stale} leads have had no activity recently`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} currency={currency} details={details} />
      {view === "table" ? (
        <>
          <ServerTable
            id="tbl-main"
            title="All leads"
            columns={columns}
            params={tableParams}
            fetcher={salesApi.leadTable}
            defaultSort="createdAt,desc"
            exportName="leads"
            onData={onLoaded}
          />

          <div className="rp-tables">
            <TrendTable
              title="Leads by date"
              data={charts.newLeadsTrend}
              series={trend}
              granularity={meta.granularity}
            />
            <DistTable
              title="Lead status"
              data={toDist(charts.byStatus)}
              nameHeader="Status"
              valueHeader="Leads"
            />
            <DistTable
              title="Where leads come from"
              data={toDist(charts.bySource)}
              nameHeader="Source"
              valueHeader="Leads"
            />
            <TablePanel
              title="Which sources work best"
              exportName="source-quality"
              columns={[
                cols.text("source", "Source"),
                cols.num("qualifiedLeads", "Good leads"),
                cols.num("convertedLeads", "Became clients"),
                cols.num("conversionRate", "Success %", "PERCENT"),
              ]}
              rows={charts.sourceQuality || []}
            />
            <TablePanel
              id="lead-owners"
              title="Owner performance"
              exportName="lead-owners"
              columns={[
                cols.text("ownerName", "Owner"),
                cols.num("leadsOwned", "Leads"),
                cols.num("converted", "Became clients"),
                cols.num("conversionRate", "Success %", "PERCENT"),
                cols.num("avgFirstResponseHours", "First reply", "HOURS"),
              ]}
              rows={charts.byOwner || []}
            />
            <DistTable
              title="By industry"
              data={toDist(charts.byIndustry)}
              nameHeader="Industry"
              valueHeader="Leads"
            />
            <TablePanel
              title="Why leads are lost"
              exportName="lost-reasons"
              columns={[
                cols.text("reason", "Reason"),
                cols.num("count", "Leads"),
                cols.num("percentage", "Share %", "PERCENT"),
              ]}
              rows={charts.lostReasons || []}
            />
            <DistTable
              title="How old open leads are"
              data={toDist(charts.leadAgeBuckets)}
              nameHeader="Time open"
              valueHeader="Leads"
            />
            <TablePanel
              title="Newly added leads"
              exportName="new-leads"
              columns={leadListColumns}
              rows={lists.recentLeads || []}
            />
            <TablePanel
              title="Hot leads"
              exportName="hot-leads"
              columns={leadListColumns}
              rows={lists.hotLeads || []}
            />
            <TablePanel
              id="stale-leads"
              title="Going stale"
              exportName="stale-leads"
              columns={leadListColumns}
              rows={lists.staleLeadsTop || []}
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Lead flow"
              subtitle="New, converted and lost"
              actions={<SeriesLegend series={trend} />}
            >
              <TrendChart
                data={charts.newLeadsTrend}
                series={trend}
                granularity={meta.granularity}
              />
            </Panel>
            <Panel title="Lead status" subtitle="Select a status to filter">
              <DonutChart
                data={toDist(charts.byStatus)}
                onSelect={(v) => setFilter("status", v)}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel title="Lead sources" subtitle="Select a source to filter">
              <DonutChart
                data={toDist(charts.bySource)}
                onSelect={(v) => setFilter("source", v)}
              />
            </Panel>
            <Panel title="Source quality" subtitle="Conversion rate by source">
              <BarList
                unit="PERCENT"
                color={PALETTE[1]}
                max={100}
                data={(charts.sourceQuality || []).map((s) => ({
                  name: s.source,
                  value: Number(s.conversionRate) || 0,
                }))}
              />
            </Panel>
          </Row>

          <Row layout="2-1">
            <Panel
              id="lead-owners"
              title="Owner performance"
              subtitle="Leads owned and converted"
            >
              <Leaderboard
                primaryLabel="leads"
                rows={[...(charts.byOwner || [])]
                  .sort((a, b) => (b.leadsOwned || 0) - (a.leadsOwned || 0))
                  .map((o) => ({
                    name: o.ownerName,
                    primary: Number(o.leadsOwned) || 0,
                    metrics: [
                      { label: "Converted", value: o.converted },
                      {
                        label: "Rate",
                        value: o.conversionRate,
                        unit: "PERCENT",
                      },
                      {
                        label: "Response",
                        value: o.avgFirstResponseHours,
                        unit: "HOURS",
                      },
                    ],
                  }))}
              />
            </Panel>
            <Panel title="Why leads are lost">
              <BarList
                data={(charts.lostReasons || []).map((r) => ({
                  name: r.reason,
                  value: Number(r.count) || 0,
                }))}
                color={PALETTE[4]}
              />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel title="By industry">
              <BarList
                data={toDist(charts.byIndustry)}
                color={PALETTE[3]}
                onSelect={(v) => setFilter("industry", v)}
              />
            </Panel>
            <Panel title="Lead age" subtitle="How long leads have been open">
              <ColumnChart
                data={toDist(charts.leadAgeBuckets)}
                color={PALETTE[2]}
              />
            </Panel>
          </Row>

          <Row layout="1-1-1">
            <Panel title="Newly added leads">
              <ItemList
                empty="No new leads"
                items={leadItems(lists.recentLeads)}
              />
            </Panel>
            <Panel title="Hot leads">
              <ItemList
                empty="No hot leads right now"
                items={leadItems(lists.hotLeads)}
              />
            </Panel>
            <Panel id="stale-leads" title="Going stale">
              <ItemList
                empty="No stale leads"
                items={leadItems(lists.staleLeadsTop)}
              />
            </Panel>
          </Row>
        </>
      )}
    </div>
  );
};

export default LeadsView;
